import { Image as ImageFromExpoImage } from "@-/com.npmjs.expo-image";
import { Platform, View } from "@-/com.npmjs.react-native";
import Animated, { type SharedValue, useAnimatedStyle } from "@-/com.npmjs.react-native-reanimated";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import Svg, {
  Circle,
  ClipPath,
  Defs,
  G,
  Path,
  RadialGradient,
  Stop,
} from "@-/com.npmjs.react-native-svg";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { getOnboardingGlobeGeometry } from "@-/dev.improve.haniverse.routes.nav.app.onboarding/onboarding-globe-geometry";
import {
  addOnboardingGlobeLines,
  type OnboardingGlobeCamera,
} from "@-/dev.improve.haniverse.routes.nav.app.onboarding/onboarding-globe-paths";
import { onboardingGlobePalette } from "@-/dev.improve.haniverse.routes.nav.app.onboarding/onboarding-globe-palette";
import { useId, useMemo } from "react";
import BOOTSPLASH_LOGO from "@-/dev.improve.haniverse.assets.logo/@images/bootsplash-logo.png";
import HANI_LOOKING_UP from "#welcome-hani-looking-up.png";
import type { WelcomeGalaxyAppearance } from "#welcome-galaxy-palette";
import { welcomeGalaxyEntryUiOpacity, type WelcomeGalaxyViewport } from "#welcome-galaxy-timeline";
import {
  getWelcomeHaniGlobeView,
  welcomeHaniWorldFrame,
  welcomeHaniWorldLayout,
} from "#welcome-hani-world-layout";

const Image = withUniwind(ImageFromExpoImage);
const UNIT_DISK = { centerX: 0, centerY: 0, radius: 1 };

/** True spherical perspective, including curved graticule and clipping at Earth's horizon. */
function globePath(
  lines: readonly Float32Array[],
  caps: Float32Array,
  camera: OnboardingGlobeCamera,
  closed = false,
) {
  const commands: string[] = [];
  addOnboardingGlobeLines(
    {
      close: () => {
        commands.push("Z");
      },
      lineTo: (x, y) => {
        commands.push(`L${x} ${y}`);
      },
      moveTo: (x, y) => {
        commands.push(`M${x} ${y}`);
      },
    },
    lines,
    camera,
    UNIT_DISK,
    closed,
    caps,
  );
  return commands.join("");
}

/**
 * Hani sits inside China's northern dip on the same north-up globe cap as the native auth.
 * Native SVG draws the shared spherical geography without a CanvasKit readiness wait.
 * Its palette follows the live theme: silver-blue on navy, graphite on warm paper.
 */
export function WelcomeHaniWorld({
  appearance,
  entryTime,
  introTime,
  launchFromSplash,
  viewport,
}: {
  appearance: WelcomeGalaxyAppearance;
  entryTime: SharedValue<number>;
  introTime: SharedValue<number>;
  launchFromSplash: boolean;
  viewport: WelcomeGalaxyViewport;
}) {
  const insets = useSafeAreaInsets();
  const { diameter } = useToolbarGridMetrics();
  const layout = welcomeHaniWorldLayout({
    bottomInset: insets.bottom,
    buttonDiameter: diameter,
    isWeb: Platform.OS === "web",
    sideInset: viewport.width > viewport.height ? Math.max(insets.left, insets.right) : 0,
    viewport,
  });
  const palette = onboardingGlobePalette(appearance);
  const id = useId().replaceAll(":", "");
  const paths = useMemo(() => {
    const geometry = getOnboardingGlobeGeometry();
    const { camera } = getWelcomeHaniGlobeView();
    return {
      china: globePath(geometry.china, geometry.caps.china, camera, true),
      graticule: globePath(geometry.graticule, geometry.caps.graticule, camera),
      land: globePath(geometry.land, geometry.caps.land, camera),
      otherMaps: globePath(geometry.otherMapOutlines, geometry.caps.otherMapOutlines, camera),
      provinces: globePath(geometry.provinceBorders, geometry.caps.provinceBorders, camera),
    };
  }, []);
  const revealStyle = useAnimatedStyle(() => ({
    opacity: welcomeGalaxyEntryUiOpacity(entryTime.get()),
  }));
  const globeStyle = useAnimatedStyle(() => {
    const frame = welcomeHaniWorldFrame(introTime.get(), layout, viewport, launchFromSplash);
    return {
      opacity: frame.globeOpacity,
      transform: [{ translateY: frame.globeTranslateY }, { scale: frame.globeScale }],
    };
  });
  const haniStyle = useAnimatedStyle(() => {
    const frame = welcomeHaniWorldFrame(introTime.get(), layout, viewport, launchFromSplash);
    return {
      transform: [
        { translateX: frame.haniTranslateX },
        { translateY: frame.haniTranslateY },
        { scale: frame.haniScale },
      ],
    };
  });
  const portraitStyle = useAnimatedStyle(() => ({
    opacity: welcomeHaniWorldFrame(introTime.get(), layout, viewport, launchFromSplash)
      .portraitOpacity,
  }));
  const splashStyle = useAnimatedStyle(() => ({
    opacity:
      1 -
      welcomeHaniWorldFrame(introTime.get(), layout, viewport, launchFromSplash).portraitOpacity,
  }));
  const { centerX, centerY, geographyDisk, haniCenterX, haniSize, haniTop, radius, stageWidth } =
    layout;
  return (
    <Animated.View
      className="absolute inset-0"
      pointerEvents="none"
      style={revealStyle}
      testID="auth-welcome-hani-world"
    >
      <View className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: stageWidth }}>
        <Animated.View
          className="absolute"
          style={[
            {
              height: radius * 2.24,
              left: centerX - radius * 1.12,
              top: centerY - radius * 1.12,
              width: radius * 2.24,
            },
            globeStyle,
          ]}
        >
          {/* Keep the full sphere in the SVG viewport so its small launch pose has no clipped base. */}
          <Svg
            height="100%"
            viewBox={`${centerX - radius * 1.12} ${centerY - radius * 1.12} ${radius * 2.24} ${radius * 2.24}`}
            width="100%"
          >
            <Defs>
              <ClipPath id={`${id}-globe`}>
                <Circle cx={centerX} cy={centerY} r={radius} />
              </ClipPath>
              <RadialGradient cx="30%" cy="27%" id={`${id}-ocean`} r="85%">
                <Stop offset="0" stopColor={palette.ocean[0]} />
                <Stop offset="1" stopColor={palette.ocean[1]} />
              </RadialGradient>
              <RadialGradient id={`${id}-atmosphere`}>
                <Stop offset="0.8" stopColor={palette.atmosphere} stopOpacity={0} />
                <Stop offset="0.9" stopColor={palette.atmosphere} />
                <Stop offset="1" stopColor={palette.atmosphere} stopOpacity={0} />
              </RadialGradient>
            </Defs>
            <Circle
              cx={centerX}
              cy={centerY}
              fill={`url(#${id}-atmosphere)`}
              opacity={appearance === "light" ? 0.35 : 0.8}
              r={radius * 1.12}
            />
            <Circle
              cx={centerX}
              cy={centerY}
              fill={`url(#${id}-ocean)`}
              r={radius}
              stroke={palette.limb}
              strokeWidth={1.2}
            />
            <G clipPath={`url(#${id}-globe)`}>
              <G
                fill="none"
                opacity={appearance === "light" ? 0.4 : 0.65}
                strokeLinecap="butt"
                strokeLinejoin="bevel"
                transform={`translate(${geographyDisk.centerX} ${geographyDisk.centerY}) scale(${geographyDisk.radius})`}
              >
                <Path
                  d={paths.graticule}
                  stroke={palette.graticule}
                  strokeWidth={0.75 / geographyDisk.radius}
                />
                <Path
                  d={paths.land}
                  stroke={palette.land}
                  strokeWidth={1.1 / geographyDisk.radius}
                />
                <Path
                  d={paths.china}
                  stroke={palette.land}
                  strokeWidth={1.2 / geographyDisk.radius}
                />
                <Path
                  d={paths.provinces}
                  stroke={palette.provinceBorder}
                  strokeWidth={0.8 / geographyDisk.radius}
                />
                <Path
                  d={paths.otherMaps}
                  stroke={palette.land}
                  strokeWidth={1.1 / geographyDisk.radius}
                />
              </G>
            </G>
          </Svg>
        </Animated.View>
      </View>
      <Animated.View
        className="absolute"
        style={[
          { height: haniSize, left: haniCenterX - haniSize / 2, top: haniTop, width: haniSize },
          haniStyle,
        ]}
        testID="auth-welcome-hani"
      >
        {launchFromSplash ? (
          <Animated.View className="absolute inset-0" style={splashStyle}>
            <Image
              className="flex-1"
              contentFit="contain"
              source={BOOTSPLASH_LOGO}
              transition={0}
            />
          </Animated.View>
        ) : null}
        <Animated.View className="absolute inset-0" style={portraitStyle}>
          <Image
            accessibilityLabel="Hani sitting on Earth, looking up at the character galaxy"
            className="flex-1"
            contentFit="contain"
            source={HANI_LOOKING_UP}
            transition={0}
          />
        </Animated.View>
      </Animated.View>
    </Animated.View>
  );
}
